import { call, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { PaymentMethodWithRedirect } from '@airwallex/airwallex-payment-elements';

import { PAYMENT_CONSENT_ACTION } from '../reducers/paymentConsent';
import { ERROR_ACTIONS } from '../reducers/error';
import { getRequest } from '../selectors';
import { getAirwayUrl, sendRequestLog } from '../../utils';
import { ExtendElementType, sendEvent, POST_EVENTS } from '../../events';
import { buildHeaders } from './confirm';

export interface CreatePaymentConsentForRedirectPayAsyncPayload {
  customerId: string;
  method: PaymentMethodWithRedirect;
  componentName: ExtendElementType;
}

export function* createPaymentConsentForRedirectAsync(payload: CreatePaymentConsentForRedirectPayAsyncPayload) {
  const { customerId, method, componentName } = payload;
  const request = yield select(getRequest);
  const reqPath = `${getAirwayUrl()}/payment_consents/create`;
  const reqPayload = {
    request_id: uuid(),
    customer_id: customerId,
    payment_method: {
      type: method,
    },
    next_triggered_by: 'merchant',
  };
  const reqHeader = buildHeaders(request);
  sendRequestLog({ componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
  const { data: paymentConsent } = yield call([axios, 'post'], reqPath, reqPayload, reqHeader);
  sendEvent(
    {
      type: componentName,
      code: POST_EVENTS.onLogResponse,
      path: reqPath,
      res: paymentConsent,
      header: {
        ...reqHeader,
        'Awx-Tracker': null,
      },
    },
    request.origin,
  );
  yield put({ type: PAYMENT_CONSENT_ACTION, payload: paymentConsent });
  return paymentConsent;
}

export interface VerifyPaymentConsentForRedirectAsyncPayload {
  consentId: string;
  componentName: ExtendElementType;
  method: PaymentMethodWithRedirect;
  successUrl: string;
}

export function* verifyPaymentConsentForRedirectAsync(payload: VerifyPaymentConsentForRedirectAsyncPayload) {
  const request = yield select(getRequest);
  const reqPath = `${getAirwayUrl()}/payment_consents/${payload.consentId}/verify`;
  const reqPayload = {
    request_id: uuid(),
    verification_options: {
      [payload.method]: {
        flow: 'webqr',
      },
    },
    return_url: payload.successUrl,
  };
  const reqHeader = buildHeaders(request);
  sendRequestLog({ componentName: payload.componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
  const { data: verifyResponse } = yield call([axios, 'post'], reqPath, reqPayload, reqHeader);
  sendEvent(
    {
      type: payload.componentName,
      code: POST_EVENTS.onLogResponse,
      path: reqPath,
      res: verifyResponse,
      header: {
        ...reqHeader,
        'Awx-Tracker': null,
      },
    },
    request.origin,
  );
  if (verifyResponse?.next_action?.type === 'redirect') {
    if (payload.componentName === 'hpp') {
      window.location.href = verifyResponse.next_action.url;
    } else {
      sendEvent(
        {
          type: payload.componentName,
          code: POST_EVENTS.onRedirect,
          redirectPayload: {
            method: payload.method,
            redirectUrl: verifyResponse.next_action.url,
          },
        },
        request?.origin,
      );
    }
  } else {
    yield put({ type: PAYMENT_CONSENT_ACTION, payload: verifyResponse });
    return verifyResponse;
  }
}

export interface CreatePaymentConsentForRedirectPayload {
  method: PaymentMethodWithRedirect;
  componentName: ExtendElementType;
  successUrl: string;
  customerId: string;
}

export function* createPaymentConsentForRedirect(data: {
  type: string;
  payload: CreatePaymentConsentForRedirectPayload;
}) {
  yield put({ type: ERROR_ACTIONS.SET, payload: {} });
  try {
    yield put({ type: PAYMENT_CONSENT_ACTION, payload: { status: 'PROCESSING' } });
    const { customerId, method, componentName, successUrl } = data?.payload || {};
    const paymentConsent = yield createPaymentConsentForRedirectAsync({
      customerId,
      method,
      componentName,
    });
    yield verifyPaymentConsentForRedirectAsync({
      consentId: paymentConsent.id,
      method,
      componentName,
      successUrl,
    });
  } catch (error) {
    yield put({ type: ERROR_ACTIONS.SET, payload: error?.response?.data });
  }
}

export function* watchPaymentConsentForRedirectAsync() {
  yield takeLatest('AWX_PAYMENT_CONSENT_FOR_REDIRECT', createPaymentConsentForRedirect);
}
